import {useState} from "react"
import {FilePlus2} from "lucide-react"
import {generatePath, useNavigate} from "react-router-dom"
import type {Sort} from "shared"

import {Button} from "@/components/ui/button"
import {createOtkDocument} from "@/lib/api/documents"
import {useDocumentsStore} from "@/store/documents"
import {useReadOnly} from "@/store/session"

/**
 * design-system.md's `NewDocumentButton` — the one entry point for creating
 * an OTK document, so it owns the label/icon/route/disabled-state contract
 * rather than letting each call site drift. documents.md's Access section:
 * this prototype does not gate the button by the `manageDrafts` permission —
 * only `readOnly`/`restricted` disable it, `readOnly` checked first, so a
 * subject that is both excluded from the register and payment-restricted
 * only ever shows the read-only tooltip (the restricted-only state has no
 * tooltip at all).
 *
 * Creating is a real `POST /otk/documents/create` (`createOtkDocument`),
 * not a client-side stand-in — a plain `Button` with an async `onClick`
 * replaces the earlier `asChild`/`NavLink` combo, which raced a real
 * navigation against the create request and pointed at a route
 * (`/documents/new`) that no longer exists. Once the draft exists
 * server-side, the ЧЕРНЕТКА tab is refetched (so the new row is there the
 * next time that tab is visible) and the new document opens directly in
 * `DocumentModal` at `/documents/edit/:id`.
 */
export function NewDocumentButton({
  restricted = false,
  sort,
}: {
  restricted?: boolean
  /** The ЧЕРНЕТКА tab's current backend sort direction (`Documents.tsx`'s `?sort=`) — the post-create refetch has to ask for the same order the tab is already showing. */
  sort: Sort
}) {
  const readOnly = useReadOnly()
  const disabled = readOnly || restricted
  const title = readOnly
    ? "Суб'єкт виключений з реєстру — доступний лише перегляд"
    : undefined
  const navigate = useNavigate()
  const refetchTab = useDocumentsStore((s) => s.refetchTab)
  const refreshCounts = useDocumentsStore((s) => s.refreshCounts)
  const [creating, setCreating] = useState(false)

  async function handleCreate() {
    if (creating) return
    setCreating(true)
    try {
      const document = await createOtkDocument()
      await Promise.all([refetchTab("ЧЕРНЕТКА", sort), refreshCounts()])
      navigate(generatePath("/documents/edit/:id", { id: document.id }))
    } finally {
      setCreating(false)
    }
  }

  if (disabled) {
    return (
      <Button disabled title={title}>
        <FilePlus2 data-icon="inline-start" />
        Новий документ
      </Button>
    )
  }

  return (
    <Button disabled={creating} onClick={handleCreate}>
      <FilePlus2 data-icon="inline-start" />
      {creating ? "Створення…" : "Новий документ"}
    </Button>
  )
}
